import React, {
  FC, useMemo,
} from 'react';
import _ from 'lodash';
import moment from 'moment';
import history from 'app/routes/history';
import classnames from 'classnames';

import {
  DropdownItem, Progress, Badge, UncontrolledDropdown, DropdownToggle, DropdownMenu,
} from 'reactstrap';

import Table from 'app/components/general/Table';
import Button from 'app/components/general/Button';
import PlusIcon from 'app/images/icons/plus.svg';
import StatusBadge from 'app/components/general/StatusBadge';
import ActionDots from 'app/components/general/ActionDots';
import { unformatAmount, getTimeRange, parseAmount } from 'app/utils/parsers';

import { ITeamMember } from 'app/ducks/teamMembers/types';

interface IProps {
  actions: any;
  teamPaymentCollectionsById: ITeamMember[];
  teamPaymentCollectionIds: string[];
  teamPaymentRequestId?: string;
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  aggressiveLoader?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  selectTeamPaymentCollection: (value) => void;
}

const PaymentCollectionsTeamMembersTable: FC<IProps> = (props: IProps = {
  manual: true,
  aggressiveLoader: false,
  actions: undefined,
  teamPaymentCollectionsById: undefined,
  teamPaymentCollectionIds: undefined,
  teamPaymentRequestId: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  pagination: undefined,
  selectTeamPaymentCollection: undefined,
}) => {
  const filterOptions = [
    {
      label: 'Status',
      accessor: 'status',
      type: 'select',
      options: [
        { id: 'pending', name: 'Unpaid' },
        { id: 'paid', name: 'Paid' },
      ],
    },
    { label: 'Team Member', accessor: 'teamMemberName', type: 'text' },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllTeamPaymentCollections(payload);
  };

  const handleRequestPayment = (): void => {
    history.push(`/payment-requests/${props.teamPaymentRequestId}/update`);
  };

  const handleClick = (teamMemberId: string): void => {
    history.push(`/team-members/detail/${teamMemberId}`);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.teamPaymentCollectionsById) || props.teamPaymentCollectionIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.teamPaymentCollectionIds,
      (obj, id) => {
        if (!_.isEmpty(props.teamPaymentCollectionsById[id])) {
          obj.push(props.teamPaymentCollectionsById[id]);
        }
        return obj;
      },
      [],
    );
    return rows;
  }, [props.teamPaymentCollectionsById, props.teamPaymentCollectionIds]);

  const renderStatus = (cellProps: {
    value: string;
  }): React.ReactNode => (
    <StatusBadge
      paymentText={cellProps.value}
    />
  );

  const renderAmount = (cellProps: {
    value: string;
  }): React.ReactNode => (
    parseAmount(cellProps.value)
  );

  const renderDate = (cellProps: {
    value: string;
  }): string => (_.isEmpty(cellProps.value) ? '' : moment(cellProps.value).format('D MMM YYYY'));

  const handleCancel = (event, pc): void => {
    event.stopPropagation();
    if (!props.isLoading) {
      if (pc.status === 'pending') {
        props.actions.updateTeamPaymentCollection({
          id: pc.id,
          status: 'cancelled',
        });
      } else if (pc.status === 'cancelled') {
        props.actions.updateTeamPaymentCollection({
          id: pc.id,
          status: 'pending',
        });
      }
    }
  };
  const handlePaidOther = (paymentType, event, pc): void => {
    event.stopPropagation();
    // console.log(paymentType);
    // console.log(props);
    // console.log(pc);
    if (!props.isLoading) {
      let status = 'pending';
      if (pc.status === 'pending') {
        switch (paymentType) {
          case 'bank':
            status = 'collected-bank-transfer';
            break;
          case 'cash':
            status = 'collected-cash';
            break;
          case 'eftpos':
            status = 'collected-eftpos';
            break;
          case 'other':
            status = 'collected-other';
            break;
          case 'undo':
            status = 'pending';
            break;
          default:
        }
      } else {
        switch (paymentType) {
          case 'undo':
            status = 'pending';
            break;
          default:
        }
      }
      const payload = {
        id: pc.id,
        status,
      };
      // console.log(payload);
      props.actions.updateTeamPaymentCollection(payload);
    }
  };

  const renderDropdownOptions = (pc: {
    status: string;
    id: string;
  }): React.ReactNode => {
    const compiledOptions = [];

    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handleCancel(e, pc)}>Cancel request</DropdownItem>);
    }
    if (pc.status === 'cancelled') {
      compiledOptions.push(<DropdownItem onClick={(e) => handleCancel(e, pc)}>Re-activate</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('bank', e, pc)}>Paid (Bank)</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('eftpos', e, pc)}>Paid (Eftpos)</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('cash', e, pc)}>Paid (Cash)</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('other', e, pc)}>Paid (Other)</DropdownItem>);
    }
    if (_.includes(['collected-cash', 'collected-other', 'collected-eftpos', 'collected-bank-transfer'], pc.status)) {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('undo', e, pc)}>Undo Payment</DropdownItem>);
    }
    return compiledOptions;
  };

  const renderAmountStatus = (cellProps: {
    value: any;
  }): React.ReactNode => {
    if (cellProps.value === undefined) {
      return null;
    }
    const options = renderDropdownOptions({
      id: cellProps.value.id,
      status: cellProps.value.status,
      paymentRequestId: cellProps.value.paymentRequest?.id,
    });
    const clickable = !_.isEmpty(options);
    return (
      <div className="d-flex flex-wrap align-items-center w-100">
        <UncontrolledDropdown
          direction="down"
          className="d-inline-block"
        >
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownToggle
              tag="div"
              className=""
              data-toggle="dropdown"
            >
              <StatusBadge
                paymentText={cellProps.value.status}
                dueDate={cellProps.value.dueAt}
                amountText={parseAmount(cellProps.value.amount)}
                // amountText={parsePaymentStatusText(cellProps.value.status)}
                tick={false}
                className={classnames('no-margin-right tag-badge', { clickable })}
              />
            </DropdownToggle>
            {clickable && (
              <DropdownMenu positionFixed>
                {options}
              </DropdownMenu>
            )}
          </div>
        </UncontrolledDropdown>
        {/* {renderStatusIcon(parsePaymentStatusText(cellProps.value.status))} */}

        {/* <span className="d-inline-block margin-left-10 text-weight-medium-bold text-black text-medium flex-grow-1">
          {parseAmount(cellProps.value.amount)}
        </span> */}
      </div>
    );
  // return (
  //   <StatusBadge
  //     paymentText={cellProps.value.status}
  //     tick={false}
  //     className="no-margin-right tag-badge"
  //   />
  // );
  };

  const renderActionDots = (cellProps: {
    value: {
      id: string;
      status: string;
    };
  }): React.ReactNode => {
    const options = renderDropdownOptions({
      id: cellProps.value.id,
      status: cellProps.value.status,
    });
    const clickable = !_.isEmpty(options);
    if (clickable) {
      return (
        <ActionDots id={cellProps?.value?.id}>
          {renderDropdownOptions({
            status: cellProps.value.status,
            id: cellProps.value.id,
          })}
        </ActionDots>
      );
    }
    return null;
  };

  const tinyColumn = 'col col-2 col-lg-1';
  const smallColumn = 'col col-3 col-lg-2';
  const growColumn = 'col col-4 col-lg-3 d-flex';
  const largeColumn = 'col col-6 col-md-5 col-lg-4 col-xl-3 d-flex';
  const mediumColumn = 'col col-4 col-md-3 col-lg-3 col-xl-3';

  const columns = useMemo(() => [
    {
      Header: 'Team Member',
      accessor: 'teamMemberName',
      className: growColumn,
      headerClassName: growColumn,
    },
    {
      id: 'status',
      Header: 'Status',
      className: largeColumn,
      headerClassName: largeColumn,
      accessor: (row): any => row,
      Cell: renderAmountStatus,
    },
    // {
    //   id: 'status',
    //   Header: 'Status',
    //   className: smallColumn,
    //   headerClassName: smallColumn,
    //   accessor: 'status',
    //   Cell: renderStatus,
    // },
    // {
    //   Header: 'Amount',
    //   accessor: 'amount',
    //   className: smallColumn,
    //   headerClassName: smallColumn,
    //   Cell: renderAmount,
    // },
    {
      Header: 'Team Name',
      accessor: 'teamName',
      className: mediumColumn,
      headerClassName: mediumColumn,
    },
    {
      Header: 'Date Paid',
      accessor: (row): any => row.purchasedAt || row.collectedOtherAt,
      className: smallColumn,
      headerClassName: smallColumn,
      Cell: renderDate,
    },
    {
      id: 'actionCol',
      Header: 'Actions',
      className: tinyColumn,
      headerClassName: tinyColumn,
      accessor: (row: any) => ({
        id: row.id,
        status: row.status,
      }),
      Cell: renderActionDots,
    },
  ], []);

  // const counterText = props.pagination.meta.totalCount === 1 ? '1 teamMember' : `${props.pagination.meta.totalCount || 0} team members`;

  return (
    <div>
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        headerTitle={props.headerTitle}
        objectTitle="payment collections"
        filterTitle="Payment Request Collections Data"
        // counterText={counterText}
        handleClick={(id, original) => {
          // console.log(id);
          // console.log(original);
          handleClick(original.teamMember?.id);
        }}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
        aggressiveLoader={props.aggressiveLoader}
      >
        <Button btnIcon={PlusIcon} onClick={handleRequestPayment} className="btn-thinner btn-2">
          Add Person to Request
        </Button>
      </Table>
    </div>
  );
};

export default PaymentCollectionsTeamMembersTable;
